'use client'

import Link from 'next/link'
import { useState } from 'react'
import { createClient } from '@/lib/supabase/client'

export default function ForgotPasswordForm() {
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!email) {
      setError('Please enter your email address')
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address')
      return
    }

    setIsLoading(true)
    try {
      const supabase = createClient()
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: 'https://www.marineconservationcredits.com/auth/reset-password',
      })
      if (resetError) {
        setError(resetError.message)
      } else {
        setSubmitted(true)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process request. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, var(--mcc-deep) 0%, var(--mcc-navy) 40%, var(--mcc-navy) 70%, var(--mcc-surface) 100%)', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", padding: '24px' }}>
      <div style={{ width: '100%', maxWidth: '440px' }}>
        {/* Logo & Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <Link href="/" style={{ textDecoration: 'none' }}>
            <div style={{ width: '64px', height: '64px', backgroundColor: 'var(--mcc-navy)', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', boxShadow: '0 8px 32px rgba(10,46,77,0.3)' }}>
              <span style={{ color: 'white', fontWeight: 800, fontSize: '24px', letterSpacing: '-0.5px' }}>MCC</span>
            </div>
          </Link>
          <h1 style={{ fontSize: '24px', fontWeight: 800, color: 'white', marginBottom: '4px', letterSpacing: '-0.3px' }}>Marine Conservation Credits</h1>
          <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)' }}>Reset Your Password</p>
        </div>

        {/* Card */}
        <div style={{ backgroundColor: 'white', borderRadius: '16px', boxShadow: '0 20px 60px rgba(0,0,0,0.15)', overflow: 'hidden' }}>
          <div style={{ padding: '36px' }}>
            {!submitted ? (
              <>
                <h2 style={{ fontSize: '26px', fontWeight: 800, color: 'var(--mcc-navy)', marginBottom: '6px' }}>Forgot Your Password?</h2>
                <p style={{ fontSize: '15px', color: 'var(--mcc-muted)', marginBottom: '28px' }}>
                  Enter your email address and we&apos;ll send you a link to reset your password
                </p>

                <form onSubmit={handleSubmit}>
                  {/* Error */}
                  {error && (
                    <div style={{ padding: '14px 16px', borderRadius: '10px', backgroundColor: 'var(--mcc-danger-bg)', border: '1px solid #fecaca', marginBottom: '20px' }}>
                      <p style={{ fontSize: '14px', fontWeight: 500, color: 'var(--mcc-danger-fg)', margin: 0 }}>{error}</p>
                    </div>
                  )}

                  {/* Email */}
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: 'var(--mcc-ink)', marginBottom: '8px' }}>
                      Email Address
                    </label>
                    <input
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={isLoading}
                      required
                      style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '2px solid var(--mcc-border)', fontSize: '15px', outline: 'none', transition: 'border-color 0.2s', backgroundColor: isLoading ? 'var(--mcc-surface)' : 'white', boxSizing: 'border-box' }}
                      onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--mcc-navy)'; }}
                      onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--mcc-border)'; }}
                    />
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isLoading}
                    style={{
                      width: '100%',
                      padding: '14px',
                      borderRadius: '8px',
                      border: 'none',
                      backgroundColor: isLoading ? 'var(--mcc-faint)' : 'var(--mcc-navy)',
                      color: 'white',
                      fontSize: '16px',
                      fontWeight: 700,
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      transition: 'background-color 0.2s',
                      boxShadow: '0 4px 12px rgba(10,46,77,0.25)',
                    }}
                    onMouseEnter={(e) => { if (!isLoading) e.currentTarget.style.backgroundColor = 'var(--mcc-navy)'; }}
                    onMouseLeave={(e) => { if (!isLoading) e.currentTarget.style.backgroundColor = 'var(--mcc-navy)'; }}
                  >
                    {isLoading ? 'Sending...' : 'Send Reset Link'}
                  </button>
                </form>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '16px 0' }}>
                {/* Success Icon */}
                <div style={{ width: '64px', height: '64px', backgroundColor: 'var(--mcc-success-bg)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--mcc-sea)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                </div>

                <h3 style={{ fontSize: '22px', fontWeight: 800, color: 'var(--mcc-navy)', marginBottom: '10px' }}>Check your email</h3>
                <p style={{ fontSize: '15px', color: 'var(--mcc-muted)', marginBottom: '8px' }}>
                  If an account exists with this email, we&apos;ve sent password reset instructions to:
                </p>
                <p style={{ fontSize: '16px', fontWeight: 700, color: 'var(--mcc-ink)', wordBreak: 'break-all', marginBottom: '24px' }}>{email}</p>

                {/* Info Box */}
                <div style={{ backgroundColor: 'var(--mcc-sky)', border: '1px solid var(--mcc-sky)', borderRadius: '10px', padding: '16px', marginBottom: '24px' }}>
                  <p style={{ fontSize: '14px', color: 'var(--mcc-teal)', margin: 0 }}>
                    The reset link will expire in 24 hours. If you don&apos;t receive an email, check your spam folder or try again.
                  </p>
                </div>

                {/* Return Button */}
                <Link href="/auth/login" style={{ textDecoration: 'none', display: 'block' }}>
                  <button
                    type="button"
                    style={{ width: '100%', padding: '14px', borderRadius: '8px', border: 'none', backgroundColor: 'var(--mcc-navy)', color: 'white', fontSize: '16px', fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 12px rgba(10,46,77,0.25)', marginBottom: '16px' }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--mcc-navy)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--mcc-navy)'; }}
                  >
                    Return to Sign In
                  </button>
                </Link>

                {/* Resend */}
                <p style={{ fontSize: '14px', color: 'var(--mcc-muted)', margin: 0 }}>
                  Didn&apos;t receive an email?{' '}
                  <button
                    onClick={() => { setSubmitted(false); setEmail(''); }}
                    style={{ background: 'none', border: 'none', color: 'var(--mcc-navy)', fontWeight: 600, cursor: 'pointer', fontSize: '14px', textDecoration: 'none', padding: 0 }}
                    onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
                  >
                    Try again
                  </button>
                </p>
              </div>
            )}
          </div>

          {/* Back to Login link (only on form view) */}
          {!submitted && (
            <div style={{ borderTop: '1px solid var(--mcc-border)', padding: '24px 36px', textAlign: 'center' }}>
              <Link href="/auth/login" style={{ textDecoration: 'none', fontSize: '14px', color: 'var(--mcc-navy)', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
                Back to Sign In
              </Link>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ textAlign: 'center', marginTop: '32px' }}>
          <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', margin: 0 }}>
            Marine Conservation Credits — Registry & Crediting Platform
          </p>
          <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.35)', marginTop: '8px' }}>
            &copy; 2026 Marine Conservation Credits. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  )
}
